import { Dispatch, FC } from 'react';
import { useTranslation } from 'react-i18next';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import ProviderAmazonFBA from '@/shared/types/fulfilment/providerAmazonFBA.type';

import { FormAction, FormState } from '../reducers/formReducer';
import FormFieldWithTooltip from './FormFieldWithTooltip';

type AmazonFBADetailsFormProps = {
  formState: FormState;
  dispatchForm: Dispatch<FormAction>;
};

const AmazonFBADetailsForm: FC<AmazonFBADetailsFormProps> = ({
  formState,
  dispatchForm,
}) => {
  const { t } = useTranslation(['fulfilmentProviders', 'common']);

  const {
    providerFBADetails: {
      lwa_client_secret,
      lwa_app_id,
      refresh_token,
      market_place_id,
      aws_secret_key,
      aws_access_key,
      role_arn,
    },
    providerFBADetailsErrors,
  } = formState;

  const onValidateField = (fieldName: string) => {
    const errors: Partial<ProviderAmazonFBA> = {};

    if (fieldName === 'lwa_client_secret') {
      !lwa_client_secret
        ? (errors.lwa_client_secret = t(
            'fulfilmentProviders:lwaClientSecretRequired',
          ))
        : (errors.lwa_client_secret = '');
    }

    if (fieldName === 'lwa_app_id') {
      !lwa_app_id
        ? (errors.lwa_app_id = t('fulfilmentProviders:lwaAppIdRequired'))
        : (errors.lwa_app_id = '');
    }

    if (fieldName === 'refresh_token') {
      !refresh_token
        ? (errors.refresh_token = t('fulfilmentProviders:refreshTokenRequired'))
        : (errors.refresh_token = '');
    }

    if (fieldName === 'market_place_id') {
      !market_place_id
        ? (errors.market_place_id = t(
            'fulfilmentProviders:marketPlaceIdRequired',
          ))
        : (errors.market_place_id = '');
    }

    dispatchForm({
      type: 'SET_PROVIDER_FBA_DETAILS_ERRORS',
      payload: { ...providerFBADetailsErrors, ...errors },
    });
  };

  return (
    <WaveUIForm_V2
      fields={[
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:lwa_client_secret'),
            value: lwa_client_secret,
            name: 'lwa_client_secret',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:lwa_client_secret')}`,
            required: true,
            error: !!providerFBADetailsErrors.lwa_client_secret,
            helperText: providerFBADetailsErrors.lwa_client_secret,
            onBlur: () => onValidateField('lwa_client_secret'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:lwa_app_id'),
            value: lwa_app_id,
            name: 'lwa_app_id',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:lwa_app_id')}`,
            required: true,
            error: !!providerFBADetailsErrors.lwa_app_id,
            helperText: providerFBADetailsErrors.lwa_app_id,
            onBlur: () => onValidateField('lwa_app_id'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:refresh_token'),
            value: refresh_token,
            name: 'refresh_token',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:refresh_token')}`,
            required: true,
            multiline: true,
            rows: 4,
            error: !!providerFBADetailsErrors.refresh_token,
            helperText: providerFBADetailsErrors.refresh_token,
            onBlur: () => onValidateField('refresh_token'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:market_place_id'),
            value: market_place_id,
            name: 'market_place_id',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:market_place_id')}`,
            required: true,
            error: !!providerFBADetailsErrors.market_place_id,
            helperText: providerFBADetailsErrors.market_place_id,
            onBlur: () => onValidateField('market_place_id'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:aws_secret_key'),
            value: aws_secret_key,
            name: 'aws_secret_key',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:aws_secret_key')}`,
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:aws_access_key'),
            value: aws_access_key,
            name: 'aws_access_key',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:aws_access_key')}`,
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:role_arn'),
            value: role_arn,
            name: 'role_arn',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:role_arn')}`,
          },
        },
      ]}
      getFieldsValues={(value) => {
        dispatchForm({
          type: 'SET_PROVIDER_FBA_DETAILS',
          payload: { ...formState.providerFBADetails, ...value },
        });
      }}
    />
  );
};

export default AmazonFBADetailsForm;
